/** Minimal shape grouping needs. Both surfaces' PurchasedProduct satisfy it structurally. */
export interface SubscriptionRow {
  shopify_subscription_id?: number | null;
}

export interface SubscriptionGroup<T extends SubscriptionRow> {
  /** Shared Shopify subscription id, or null for a one-time / non-subscription row. */
  subscriptionId: number | null;
  /** Line items for this subscription, in original order. Null-id rows are singletons. */
  products: T[];
}

/**
 * Fold a flat list of purchased-product rows into one group per subscription.
 * Rows sharing a non-null `shopify_subscription_id` collapse into a single group
 * (positioned at the first member's index). Rows with a null/undefined id each
 * become their own standalone singleton group — never merged.
 */
export function groupSubscriptions<T extends SubscriptionRow>(
  rows: T[],
): SubscriptionGroup<T>[] {
  const groups: SubscriptionGroup<T>[] = [];
  const indexById = new Map<number, number>();

  for (const row of rows) {
    const id = row.shopify_subscription_id;
    if (id == null) {
      groups.push({ subscriptionId: null, products: [row] });
      continue;
    }
    const at = indexById.get(id);
    if (at === undefined) {
      indexById.set(id, groups.length);
      groups.push({ subscriptionId: id, products: [row] });
    } else {
      groups[at].products.push(row);
    }
  }

  return groups;
}
